import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { promptsAPI, adminAPI } from '../services/api';
import { estimateTokens, formatTokenCount, MAX_PROMPT_LENGTH } from '../utils/tokenCounter';
import './EditPrompt.css';

const PREDEFINED_TAGS = ['Writing', 'Coding', 'Data', 'Business', 'Fun', 'Education', 'Marketing', 'Research', 'Creative', 'Technical'];

function EditPrompt({ currentUsername, showToast, isAdmin = false, adminSession = null }) {
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [promptText, setPromptText] = useState('');
  const [category, setCategory] = useState('');
  const [model, setModel] = useState('');
  const [toneStyle, setToneStyle] = useState('');
  const [useCase, setUseCase] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const tokenEstimate = useMemo(() => {
    return formatTokenCount(estimateTokens(promptText));
  }, [promptText]);

  const handleTagClick = (tag) => {
    const tags = category ? category.split(',').map(t => t.trim()) : [];
    if (tags.includes(tag)) {
      setCategory(tags.filter(t => t !== tag).join(', '));
    } else {
      setCategory([...tags, tag].join(', '));
    }
  };

  useEffect(() => {
    loadPrompt();
  }, [id]);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const response = await promptsAPI.getById(id);
      const prompt = response.data;
      setTitle(prompt.title || '');
      setPromptText(prompt.prompt_text || prompt.promptText || '');
      setCategory(prompt.category || '');
      setModel(prompt.model || '');
      setToneStyle(prompt.tone_style || prompt.toneStyle || '');
      setUseCase(prompt.use_case || prompt.useCase || '');
    } catch (err) {
      setError('Failed to load prompt');
      showToast('Failed to load prompt', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUsername && !isAdmin) {
      setError('Please set your username first');
      showToast('Please set your username first', 'error');
      return;
    }

    setSaving(true);
    setError(null);

    // Frontend validation
    if (promptText.length > MAX_PROMPT_LENGTH) {
      setError(`Prompt text exceeds maximum length of ${MAX_PROMPT_LENGTH} characters`);
      showToast(`Prompt text exceeds maximum length of ${MAX_PROMPT_LENGTH} characters`, 'error');
      setSaving(false);
      return;
    }

    try {
      const updateData = {
        title,
        prompt_text: promptText,
        category: category.trim(),
        model: model.trim() || null,
        tone_style: toneStyle.trim() || null,
        use_case: useCase.trim() || null,
        username: currentUsername || 'admin',
      };

      // Check if user is admin (would need to pass session from AdminPanel)
      // For now, use regular update which checks ownership
      await promptsAPI.update(id, updateData);
      
      showToast('Prompt updated successfully!', 'success');
      navigate(`/prompts/${id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update prompt';
      setError(errorMsg);
      showToast(errorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="container">
      <h1>Edit Prompt</h1>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit} className="edit-prompt-form">
        <div className="form-group">
          <label className="label">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            required
          />
        </div>
        <div className="form-group">
          <label className="label">Prompt Text</label>
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            className="textarea"
            rows={10}
            placeholder="Enter the exact prompt text to use with AI (plain text only)."
            required
          />
          <div className="text-counter">
            <span className={promptText.length > MAX_PROMPT_LENGTH ? 'text-danger' : ''}>
              {promptText.length} / {MAX_PROMPT_LENGTH} characters
            </span>
            <span>{tokenEstimate}</span>
          </div>
        </div>
        <div className="form-group">
          <label className="label">Tags (select or type comma-separated)</label>
          <div className="tag-selector">
            <div className="tag-pills">
              {PREDEFINED_TAGS.map(tag => {
                const tags = category ? category.split(',').map(t => t.trim()) : [];
                const isSelected = tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagClick(tag)}
                    className={`tag-pill ${isSelected ? 'selected' : ''}`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="input"
              placeholder="Or type tags separated by commas (e.g., Writing, Business)"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="label">Model (optional)</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="input"
            placeholder="e.g., GPT-4, Claude, Gemini"
          />
        </div>
        <div className="form-group">
          <label className="label">Tone / Style (optional)</label>
          <input
            type="text"
            value={toneStyle}
            onChange={(e) => setToneStyle(e.target.value)}
            className="input"
            placeholder="e.g., formal, playful, professional"
          />
        </div>
        <div className="form-group">
          <label className="label">Use Case (optional)</label>
          <input
            type="text"
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            className="input"
            placeholder="e.g., data analysis, email reply, code review"
          />
        </div>
        <button type="submit" className="button button-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="button"
          style={{ marginLeft: '10px' }}
        >
          Cancel
        </button>
      </form>
    </div>
  );
}

export default EditPrompt;
